import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Participant } from '../App';
import UserAnalyticsApiService, { UserAnalytics, UserConversationAnalytics } from '../services/userAnalyticsApi';
import AnalyticsApiService from '../services/analyticsApi';
import { processParticipant, convertLegacyGcsUrlToProxy } from '../utils/profileImage';

interface ProfileOverlayProps {
  participant: Participant;
  isOpen: boolean;
  onClose: () => void;
  conversationId?: number; // Optional for backward compatibility
}

function QuickStatCard({ 
  title, 
  value, 
  unit, 
  isLoading 
}: { 
  title: string;
  value: string;
  unit: string;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border border-gray-200/40 dark:border-gray-700/40 rounded-2xl p-4">
        <div className="space-y-2">
          <Skeleton className="h-3 w-16 bg-gray-200/80 dark:bg-gray-700/80 rounded" />
          <Skeleton className="h-6 w-12 bg-gray-200/80 dark:bg-gray-700/80 rounded" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border border-gray-200/40 dark:border-gray-700/40 rounded-2xl p-4 hover:bg-white dark:hover:bg-gray-800 transition-all duration-200"
    >
      <p className="text-xs font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-gray-500 dark:text-gray-400 mb-2">
        {title}
      </p>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[700] text-gray-900 dark:text-gray-100 tracking-[-0.2px]">
          {value}
        </span>
        {unit && (
          <span className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-gray-500 dark:text-gray-400">
            {unit}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function ProfileOverlay({ participant, isOpen, onClose, conversationId }: ProfileOverlayProps) {
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [conversationAnalytics, setConversationAnalytics] = useState<UserConversationAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvedUsername, setResolvedUsername] = useState<string | null>(null);
  
  // Process participant data to ensure profile images use proxy URLs
  const processedParticipant = processParticipant(participant);

  // Resolve username with priority: participant.username > conversation fetch (prefer humans) > any human with username > profileUrl extraction
  const resolveUsername = async (): Promise<string | null> => {
    console.log('[ProfileOverlay] Resolving username for participant:', {
      id: participant.id,
      name: participant.name,
      username: participant.username,
      role: participant.role,
      phoneNumber: participant.phoneNumber
    });
    
    // Priority 1: Use participant username if available
    if (participant.username) {
      console.log('[ProfileOverlay] Using participant.username:', participant.username);
      return participant.username;
    }

    // Priority 2: Fetch from conversation endpoint (backend now includes username)
    if (conversationId) {
      try {
        console.log('[ProfileOverlay] Fetching conversation:', conversationId);
        const conv = await AnalyticsApiService.getConversation(conversationId);
        const participants: any[] = Array.isArray(conv?.participants) ? conv.participants : [];
        
        console.log('[ProfileOverlay] Conversation participants:', participants.map(p => ({
          id: p.id,
          name: p.name,
          username: p.username,
          role: p.role,
          kind: p.kind,
          phone: p.phone
        })));

        // Prefer matching among humans only
        // Support both old API (role='human') and new API (kind='user' or role='user')
        const isHumanParticipant = (p: any) => 
          p?.role === 'human' || p?.role === 'user' || p?.kind === 'user';
        
        const humans = participants.filter(isHumanParticipant);
        console.log('[ProfileOverlay] Human participants found:', humans.length);

        // First try ID match among humans if participant.id exists
        if (participant.id != null) {
          const idMatch = humans.find((p: any) => String(p.id) === String(participant.id));
          if (idMatch?.username) {
            console.log('[ProfileOverlay] Found username by ID match:', idMatch.username);
            return idMatch.username;
          }
        }

        // Then try phone match among humans
        if (participant?.phoneNumber) {
          const normalizedTarget = String(participant.phoneNumber).replace(/\D/g, '');
          const phoneMatch = humans.find((p: any) => p?.phone && String(p.phone).replace(/\D/g, '') === normalizedTarget);
          if (phoneMatch?.username) {
            console.log('[ProfileOverlay] Found username by phone match:', phoneMatch.username);
            return phoneMatch.username;
          }
        }

        // Fallback: if any human participant has a username, use that
        const anyHumanWithUsername = humans.find((p: any) => p?.username);
        if (anyHumanWithUsername?.username) {
          console.log('[ProfileOverlay] Found username from any human:', anyHumanWithUsername.username);
          return anyHumanWithUsername.username;
        }
        
        console.log('[ProfileOverlay] No username found in conversation participants');
      } catch (error) {
        console.warn('[ProfileOverlay] Failed to fetch username from conversation:', error);
      }
    }

    // Priority 3: Try fetching user profile by ID to get username
    if (participant.id) {
      try {
        const userId = typeof participant.id === 'string' ? parseInt(participant.id, 10) : participant.id;
        if (!isNaN(userId)) {
          const userProfile = await AnalyticsApiService.getUser(userId);
          console.log('[ProfileOverlay] Fetched user profile:', userProfile);
          if (userProfile?.username) {
            return userProfile.username;
          }
          // Also check for displayUsername or display_username
          if (userProfile?.displayUsername || userProfile?.display_username) {
            return userProfile.displayUsername || userProfile.display_username;
          }
        }
      } catch (error) {
        console.warn('[ProfileOverlay] Failed to fetch user profile:', error);
      }
    }
    
    // Priority 4: Extract from analytics profileUrl as last resort
    if (analytics?.profileUrl) {
      const extracted = extractUsernameFromProfileUrl(analytics.profileUrl);
      if (extracted) {
        console.log('[ProfileOverlay] Extracted username from profileUrl:', extracted);
        return extracted;
      }
    }

    console.log('[ProfileOverlay] No username found after all attempts');
    return null;
  };

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const userKey = participant.id;
      
      if (conversationId) {
        // Fetch conversation-specific analytics
        const conversationData = await UserAnalyticsApiService.getUserConversationAnalytics(userKey, conversationId);
        setConversationAnalytics(conversationData);
        
        // Fetch general user analytics
        const generalData = await UserAnalyticsApiService.getUserAnalytics(userKey);
        setAnalytics(generalData);
      } else {
        // Fallback to general analytics only
        const data = await UserAnalyticsApiService.getUserAnalytics(userKey);
        setAnalytics(data);
        setConversationAnalytics(null);
      }

      // Resolve username after analytics are loaded
      const username = await resolveUsername();
      setResolvedUsername(username);
    } catch (error) {
      console.error('Failed to fetch user analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && participant) {
      // Reset state when opening
      setResolvedUsername(participant.username || null);
      
      if (analytics && analytics.userId !== participant.id) {
        setAnalytics(null);
      }
      
      fetchAnalytics();
    }
  }, [isOpen, participant.id]);

  // Handle escape key to close overlay
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Generate initials for fallback
  const initials = processedParticipant.name
    .split(' ')
    .map((word: string) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getSentimentColor = (sentiment: string): string => {
    switch (sentiment) {
      case 'positive': return 'bg-green-100 text-green-700 border-green-200';
      case 'negative': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Helper function to extract username from profile URL
  const extractUsernameFromProfileUrl = (url?: string): string | null => {
    if (!url) return null;
    try {
      const u = new URL(url, 'https://series.so');
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('profile');
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
      if (parts.length === 1) return parts[0];
    } catch {
      console.warn('Failed to parse profile URL:', url);
    }
    return null;
  };

  // Get the best available username (from resolved or participant)
  const getDisplayUsername = (): string | null => {
    return resolvedUsername || participant.username || null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl z-50 rounded-3xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Full Container Layout */}
          <div className="w-full h-full flex flex-col">
            {/* Header - Fixed Height */}
            <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-gray-50/80 to-white/80 dark:from-gray-900/80 dark:to-gray-950/80 border-b border-gray-200/30 dark:border-gray-700/30 flex-shrink-0">
              {/* Close Button - Top Right */}
              <Button
                onClick={onClose}
                variant="ghost"
                className="absolute top-4 right-4 w-8 h-8 p-0 rounded-xl bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 shadow-sm border border-gray-200/50 dark:border-gray-700/50 z-10"
              >
                <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>

              {/* Profile Info - Centered */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-300/90 to-gray-400/90 ring-3 ring-white shadow-lg">
                    {processedParticipant.profileImage ? (
                      <ImageWithFallback
                        src={processedParticipant.profileImage}
                        alt={processedParticipant.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-gray-700 font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[600] text-xl">
                          {initials}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Online status */}
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-3 border-white shadow-sm">
                    <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30"></div>
                  </div>
                </div>

                <div className="flex-1 space-y-1">
                  <h1 className="text-3xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[700] text-gray-900 dark:text-gray-100 tracking-[-0.4px]">
                    {processedParticipant.name}
                  </h1>
                  <p className="text-lg font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-gray-600 dark:text-gray-400">
                    {processedParticipant.role || 'Team Member'}
                  </p>
                  <p className="text-base font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[400] text-gray-500 dark:text-gray-400">
                    {processedParticipant.phoneNumber}
                  </p>
                  {getDisplayUsername() ? (
                    <a 
                      href={`https://series.so/profile/${getDisplayUsername()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Full Profile
                    </a>
                  ) : (
                    <div className="inline-flex items-center gap-1 text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-gray-400 dark:text-gray-500">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isLoading ? 'Loading profile...' : 'Profile unavailable'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area - Flexible */}
            <div className="flex-1 px-6 py-6 space-y-6 overflow-y-auto">
              {/* Quick Stats */}
              <div>
                <h2 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-[600] text-gray-900 dark:text-gray-100 tracking-[-0.2px] mb-4">
                  Activity Overview
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <QuickStatCard
                    title="Messages"
                    value={conversationAnalytics ? 
                      conversationAnalytics.messagesInConversation.toLocaleString() : 
                      (analytics?.activitySummary.totalMessages.toLocaleString() || '0')
                    }
                    unit={conversationAnalytics ? "in chat" : "sent"}
                    isLoading={isLoading}
                  />
                  <QuickStatCard
                    title="Response"
                    value={conversationAnalytics ? 
                      conversationAnalytics.responseTimeMinutes.toString() : 
                      (analytics?.activitySummary.averageResponseTime.toString() || '0')
                    }
                    unit="min avg"
                    isLoading={isLoading}
                  />
                  <QuickStatCard
                    title="Groups"
                    value={conversationAnalytics ? 
                      conversationAnalytics.totalGroupChats.toString() : 
                      (analytics?.activitySummary.totalGroupChats.toString() || '0')
                    }
                    unit="joined"
                    isLoading={isLoading}
                  />
                  <QuickStatCard
                    title="Active Since"
                    value={conversationAnalytics ? 
                      formatDate(new Date(conversationAnalytics.lastActive)) : 
                      (analytics ? formatDate(analytics.activitySummary.activeSince) : 'Unknown')
                    }
                    unit=""
                    isLoading={isLoading}
                  />
                </div>
              </div>

              {/* Sentiment */}
              {(conversationAnalytics || analytics) && (
                <motion.div 
                  className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-2xl border border-gray-100/50 dark:border-gray-700/50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div>
                    <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[500] text-gray-600 dark:text-gray-400 mb-1">
                      Communication Sentiment
                    </p>
                    <p className="text-lg font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[600] text-gray-900 dark:text-gray-100">
                      {conversationAnalytics ? 
                        `${conversationAnalytics.positivePercentage}% positive interactions` :
                        `${analytics?.sentimentAnalysis.positivePercentage}% positive interactions`
                      }
                    </p>
                  </div>
                  <Badge className={`px-4 py-2 rounded-xl font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[600] ${getSentimentColor(
                    conversationAnalytics ? 
                      (conversationAnalytics.positivePercentage >= 50 ? 'positive' : 
                       conversationAnalytics.positivePercentage <= 30 ? 'negative' : 'neutral') :
                      (analytics?.sentimentAnalysis.overall || 'neutral')
                  )}`}>
                    {conversationAnalytics ? 
                      (conversationAnalytics.positivePercentage >= 50 ? 'positive' : 
                       conversationAnalytics.positivePercentage <= 30 ? 'negative' : 'neutral') :
                      (analytics?.sentimentAnalysis.overall || 'neutral')
                    }
                  </Badge>
                </motion.div>
              )}
            </div>

            {/* Footer - Fixed Height */}
            {/* <div className="px-6 pb-6 flex-shrink-0">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-['SF_Pro_Text',_-apple-system,_sans-serif] font-[600] text-lg"
                onClick={() => {
                  if (analytics?.profileUrl) {
                    window.open(analytics.profileUrl, '_blank');
                  }
                }}
              >
                 View Full Profile
              </Button>    
            </div> */}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}